/* =======================================================
   UNEW Academy – Consolidated Renderer (SINGLE-LANGUAGE)
   File: assets/unew-academy.js
   Scope: .unew-academy namespace

   Blocks:
   - HERO:       [data-unew-academy-hero]    + script.unew-academy-hero-config
   - SUBHERO:    [data-unew-academy-subhero] + script.unew-academy-subhero-config
   - PRODUCT:    [data-unew-academy-product] + script.unew-academy-product-config
   - INFO+AUDIO: [data-unew-academy-infoaudio] + script.unew-academy-infoaudio-config

   Rendering targets (created if missing):
   - .unew-academy__hero-render
   - .unew-academy__subhero-render
   - .unew-academy__product-render
   - .unew-academy__infoaudio-render

   Principles:
   - Non-destructive: if target already has markup, do nothing.
   - Deterministic: no reliance on legacy functions.
   - Performance: rAF scheduling + MutationObserver.
   ======================================================= */
(function () {
  "use strict";

  var SEL = {
    HERO_BLOCK: "[data-unew-academy-hero]",
    HERO_CFG: "script.unew-academy-hero-config",
    HERO_TARGET: ".unew-academy__hero-render",

    SUB_BLOCK: "[data-unew-academy-subhero]",
    SUB_CFG: "script.unew-academy-subhero-config",
    SUB_TARGET: ".unew-academy__subhero-render",

    PROD_BLOCK: "[data-unew-academy-product]",
    PROD_CFG: "script.unew-academy-product-config",
    PROD_TARGET: ".unew-academy__product-render",

    INFO_BLOCK: "[data-unew-academy-infoaudio]",
    INFO_CFG: "script.unew-academy-infoaudio-config",
    INFO_TARGET: ".unew-academy__infoaudio-render"
  };

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /* -----------------------------------------------------
     Inline formatting for authored text (JSON items)
     Allows a minimal, safe set of tags:
       - <strong>...</strong>
       - <em>...</em>
       - <br>
     Everything else remains escaped.

     Robustness additions:
       - Case-insensitive tag recovery (<BR>, <Strong>, etc.)
       - Supports all <br> variants: <br>, <br/>, <br />
       - Converts newline characters (\n) into <br>
     ----------------------------------------------------- */
  function fmtInline(s) {
    if (s == null) return "";

    // Normalize newlines first so authors can use Enter in JSON text
    var raw = String(s).replace(/\r\n/g, "\n").replace(/\r/g, "\n");

    // Escape everything, then selectively "unescape" our whitelist
    var out = esc(raw);

    // Convert escaped newlines to <br>
    out = out.replace(/\n+/g, "<br>");

    // strong (case-insensitive)
    out = out.replace(/&lt;strong&gt;/gi, "<strong>");
    out = out.replace(/&lt;\/strong&gt;/gi, "</strong>");

    // em (case-insensitive)
    out = out.replace(/&lt;em&gt;/gi, "<em>");
    out = out.replace(/&lt;\/em&gt;/gi, "</em>");

    // br (case-insensitive; supports <br>, <br/>, <br />)
    out = out.replace(/&lt;br\s*\/?&gt;/gi, "<br>");

    return out;
  }

  function parseJsonScript(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent || ""); } catch (e) { return null; }
  }

  function ensureTarget(block, selector, className) {
    var t = block.querySelector(selector);
    if (t) return t;
    t = document.createElement("div");
    t.className = className;
    block.appendChild(t);
    return t;
  }

  function isEmpty(node) {
    return !node || (node.innerHTML || "").trim().length === 0;
  }

  function closestCard(el) {
    if (!el || !el.closest) return null;
    return el.closest(".unew-academy__card");
  }

  function wrapWithCardIfMissing(block, innerHtml) {
    if (closestCard(block)) return innerHtml;
    return '<section class="unew-academy__card">' + innerHtml + "</section>";
  }

  function guessAudioMime(url) {
    var u = String(url || "").toLowerCase();
    if (u.indexOf(".m4a") > -1 || u.indexOf(".mp4") > -1) return "audio/mp4";
    if (u.indexOf(".wav") > -1) return "audio/wav";
    if (u.indexOf(".ogg") > -1) return "audio/ogg";
    if (u.indexOf(".webm") > -1) return "audio/webm";
    return "audio/mpeg";
  }

  function guessVideoMime(url) {
    var u = String(url || "").toLowerCase();
    if (u.indexOf(".mp4") > -1 || u.indexOf(".m4v") > -1) return "video/mp4";
    if (u.indexOf(".webm") > -1) return "video/webm";
    if (u.indexOf(".mov") > -1) return "video/quicktime";
    return "video/mp4";
  }

  // -------------------------------------------------------
  // HERO
  // -------------------------------------------------------
  function renderHeroes() {
    var blocks = document.querySelectorAll(SEL.HERO_BLOCK);
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (block.getAttribute("data-unew-hero-done") === "1") continue;

      var cfg = parseJsonScript(block.querySelector(SEL.HERO_CFG));
      if (!cfg) continue;

      var target = ensureTarget(block, SEL.HERO_TARGET, "unew-academy__hero-render");
      if (!isEmpty(target)) {
        block.setAttribute("data-unew-hero-done", "1");
        continue;
      }

      var pageTitle = cfg.page_title || "";
      var title = cfg.title || "";
      var text = cfg.text || "";
      var image = cfg.image || "";
      var chips = Array.isArray(cfg.chips) ? cfg.chips : [];

      var chipsHtml = chips.length
        ? '<div class="unew-academy__hero-chips">' +
            chips.map(function (c) { return '<span class="unew-academy__chip">' + esc(c) + "</span>"; }).join("") +
          "</div>"
        : "";

      var pageTitleHtml = pageTitle ? '<div class="unew-academy__page-title">' + esc(pageTitle) + "</div>" : "";

      var heroHtml =
        pageTitleHtml +
        '<section class="unew-academy__hero">' +
          '<div class="unew-academy__hero-bg">' +
            (image ? '<img class="unew-academy__hero-bg-img" src="' + esc(image) + '" alt="" loading="eager" fetchpriority="high">' : "") +
          "</div>" +
          '<div class="unew-academy__hero-overlay" aria-hidden="true"></div>' +
          '<div class="unew-academy__hero-inner">' +
            '<div class="unew-academy__hero-content">' +
              (title ? '<h1 class="unew-academy__hero-title">' + esc(title) + "</h1>" : "") +
              (text ? '<p class="unew-academy__hero-text">' + esc(text) + "</p>" : "") +
              '<div class="unew-academy__hero-spacer"></div>' +
              chipsHtml +
            "</div>" +
          "</div>" +
        "</section>";

      target.innerHTML = wrapWithCardIfMissing(block, heroHtml);
      block.setAttribute("data-unew-hero-done", "1");
    }
  }

  // -------------------------------------------------------
  // SUBHERO
  // -------------------------------------------------------
  function renderSubHeros() {
    var blocks = document.querySelectorAll(SEL.SUB_BLOCK);
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (block.getAttribute("data-unew-subhero-done") === "1") continue;

      var cfg = parseJsonScript(block.querySelector(SEL.SUB_CFG));
      if (!cfg) continue;

      var target = ensureTarget(block, SEL.SUB_TARGET, "unew-academy__subhero-render");
      if (!isEmpty(target)) {
        block.setAttribute("data-unew-subhero-done", "1");
        continue;
      }

      var title = cfg.title || "";
      var image = cfg.image || "";

      var subHtml =
        '<section class="unew-academy__subhero">' +
          '<div class="unew-academy__subhero-card">' +
            (title ? '<div class="unew-academy__subhero-title">' + esc(title) + "</div>" : "") +
            '<div class="unew-academy__subhero-body">' +
              (image ? '<img class="unew-academy__subhero-img" src="' + esc(image) + '" alt="' + esc(title) + '" loading="lazy">' : "") +
            "</div>" +
          "</div>" +
        "</section>";

      target.innerHTML = wrapWithCardIfMissing(block, subHtml);
      block.setAttribute("data-unew-subhero-done", "1");
    }
  }

  // -------------------------------------------------------
  // PRODUCTS
  // -------------------------------------------------------

  function titleCaseKey(k) {
    var s = String(k || "").trim();
    if (!s) return "";
    s = s.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
    return s.split(" ").map(function (w) {
      return w ? (w.charAt(0).toUpperCase() + w.slice(1)) : "";
    }).join(" ");
  }

  function normalizeBadges(arr) {
    if (!Array.isArray(arr)) return [];
    return arr.map(function (b) {
      if (typeof b === "string") {
        var key = String(b || "").trim().toLowerCase();
        if (!key) return null;
        if (key === "base" || key === "strength" || key === "length") {
          return { type: "defined", key: key };
        }
        return { type: "free", key: key, label: titleCaseKey(key), tone: "default" };
      }

      if (b && typeof b === "object") {
        var ok = String(b.key || "").trim().toLowerCase();
        if (!ok) return null;
        return {
          type: "free",
          key: ok,
          label: String(b.label || titleCaseKey(ok) || ok).trim(),
          tone: String(b.tone || "default").trim().toLowerCase()
        };
      }

      return null;
    }).filter(Boolean);
  }

  function badgeLabel(k) {
    var map = { base: "Base", strength: "Strength", length: "Length" };
    return map[k] || k;
  }

  function badgeClass(k) {
    if (k === "base") return "unew-academy__badge unew-academy__badge--base";
    if (k === "strength") return "unew-academy__badge unew-academy__badge--strength";
    if (k === "length") return "unew-academy__badge unew-academy__badge--length";
    return "unew-academy__badge";
  }

  function badgeIconClass(k) {
    if (k === "base") return "unew-academy__badge-ico unew-academy__badge-ico--base";
    if (k === "strength") return "unew-academy__badge-ico unew-academy__badge-ico--strength";
    if (k === "length") return "unew-academy__badge-ico unew-academy__badge-ico--length";
    return "unew-academy__badge-ico";
  }

  var FREE_BADGE_TONES = {
    "default": {
      fg: "var(--unew-academy-tone-default-fg)",
      bg: "var(--unew-academy-tone-default-bg)",
      br: "var(--unew-academy-tone-default-br)"
    },

    "unew-scarlet": {
      fg: "var(--unew-academy-tone-unew-scarlet-fg)",
      bg: "var(--unew-academy-tone-unew-scarlet-bg)",
      br: "var(--unew-academy-tone-unew-scarlet-br)"
    },
    "unew-navy": {
      fg: "var(--unew-academy-tone-unew-navy-fg)",
      bg: "var(--unew-academy-tone-unew-navy-bg)",
      br: "var(--unew-academy-tone-unew-navy-br)"
    },
    "nouveau": {
      fg: "var(--unew-academy-tone-nouveau-fg)",
      bg: "var(--unew-academy-tone-nouveau-bg)",
      br: "var(--unew-academy-tone-nouveau-br)"
    },
    "hdbrows": {
      fg: "var(--unew-academy-tone-hdbrows-fg)",
      bg: "var(--unew-academy-tone-hdbrows-bg)",
      br: "var(--unew-academy-tone-hdbrows-br)"
    },

    "ethos-blush": {
      fg: "var(--unew-academy-tone-ethos-blush-fg)",
      bg: "var(--unew-academy-tone-ethos-blush-bg)",
      br: "var(--unew-academy-tone-ethos-blush-br)"
    },
    "ethos-sand": {
      fg: "var(--unew-academy-tone-ethos-sand-fg)",
      bg: "var(--unew-academy-tone-ethos-sand-bg)",
      br: "var(--unew-academy-tone-ethos-sand-br)"
    },
    "ethos-mint": {
      fg: "var(--unew-academy-tone-ethos-mint-fg)",
      bg: "var(--unew-academy-tone-ethos-mint-bg)",
      br: "var(--unew-academy-tone-ethos-mint-br)"
    },
    "ethos-lilac": {
      fg: "var(--unew-academy-tone-ethos-lilac-fg)",
      bg: "var(--unew-academy-tone-ethos-lilac-bg)",
      br: "var(--unew-academy-tone-ethos-lilac-br)"
    }
  };

  function freeBadgeStyle(toneKey) {
    var t = FREE_BADGE_TONES[toneKey] || FREE_BADGE_TONES["default"];
    return "--b-fg:" + t.fg + ";--b-bg:" + t.bg + ";--b-br:" + t.br + ";";
  }

  function renderBadgeHTML(obj) {
    if (!obj) return "";

    if (obj.type === "defined") {
      var k = obj.key;
      return '<span class="' + badgeClass(k) + '">' +
        '<span class="' + badgeIconClass(k) + '" aria-hidden="true"></span>' +
        esc(badgeLabel(k)) +
      "</span>";
    }

    var label = obj.label || titleCaseKey(obj.key) || obj.key;
    var tone = obj.tone || "default";
    var style = freeBadgeStyle(tone);

    return '<span class="unew-academy__badge unew-academy__badge--free" style="' + esc(style) + '">' +
      '<span class="unew-academy__badge-ico unew-academy__badge-ico--free" aria-hidden="true"></span>' +
      esc(label) +
    "</span>";
  }

  function renderCornerImageHTML(cfg) {
    var c = cfg && cfg.corner_image && typeof cfg.corner_image === "object" ? cfg.corner_image : null;
    if (!c || !c.src) return "";
    return (
      '<div class="unew-academy__corner-image">' +
        '<img src="' + esc(c.src) + '" alt="' + esc(c.alt || "") + '" loading="lazy">' +
      "</div>"
    );
  }

  function renderSingleMediaHTML(cfg) {
    var m = cfg && cfg.media && typeof cfg.media === "object" ? cfg.media : null;
    if (!m || !m.src || !m.type) return "";

    var type = String(m.type || "").trim().toLowerCase();
    if (type !== "audio" && type !== "video") return "";

    var label = m.label || "";
    var autoplay = m.autoplay === true;
    var muted = m.muted === true;
    var loop = m.loop === true;
    var controls = (m.controls !== false);
    var poster = m.poster || "";

    var attrs =
      (controls ? " controls" : "") +
      (autoplay ? " autoplay" : "") +
      (muted ? " muted" : "") +
      (loop ? " loop" : "") +
      " preload=\"metadata\" playsinline";

    if (type === "video" && poster) {
      attrs += " poster=\"" + esc(poster) + "\"";
    }

    if (type === "video") {
      var vm = guessVideoMime(m.src);
      return (
        '<div class="unew-academy__media-wrap">' +
          (label ? '<div class="unew-academy__media-label">' + esc(label) + "</div>" : "") +
          '<video class="unew-academy__media unew-academy__media--video"' + attrs + ">" +
            '<source src="' + esc(m.src) + '" type="' + esc(vm) + '">' +
          "</video>" +
        "</div>"
      );
    }

    var am = guessAudioMime(m.src);
    return (
      '<div class="unew-academy__media-wrap">' +
        (label ? '<div class="unew-academy__media-label">' + esc(label) + "</div>" : "") +
        '<audio class="unew-academy__media unew-academy__media--audio"' + attrs + ">" +
          '<source src="' + esc(m.src) + '" type="' + esc(am) + '">' +
        "</audio>" +
      "</div>"
    );
  }

  function renderSpecSheetHTML(cfg) {
    var title = cfg.title || "";
    var image = cfg.image || "";
    var topTag = (cfg.top_tag && typeof cfg.top_tag === "object") ? cfg.top_tag : null;
    var tagLabel = topTag ? (topTag.label || "") : "";
    var tagIcon = topTag ? (topTag.icon || "") : "";
    var sections = Array.isArray(cfg.sections) ? cfg.sections : [];

    var sectionsHtml = sections.map(function (sec) {
      if (!sec || typeof sec !== "object") return "";
      var st = sec.title || "";
      var items = Array.isArray(sec.items) ? sec.items : [];
      if (!st && !items.length) return "";
      return (
        '<div class="unew-academy__specsheet-section">' +
          '<div class="unew-academy__specsheet-section-title">' + esc(st) + "</div>" +
          '<ul class="unew-academy__specsheet-list">' +
            items.map(function (it) { return "<li>" + fmtInline(it) + "</li>"; }).join("") +
          "</ul>" +
        "</div>"
      );
    }).join("");

    return (
      '<div class="unew-academy__specsheet">' +
        '<div class="unew-academy__specsheet-media">' +
          '<div class="unew-academy__specsheet-imgwrap">' +
            (image ? '<img class="unew-academy__specsheet-img" src="' + esc(image) + '" alt="' + esc(title) + '" loading="lazy">' : "") +
          "</div>" +
        "</div>" +
        '<div class="unew-academy__specsheet-body">' +
          '<div class="unew-academy__specsheet-head">' +
            '<h2 class="unew-academy__specsheet-title">' + esc(title) + "</h2>" +
            (tagLabel
              ? '<div class="unew-academy__specsheet-tag">' +
                  '<span class="unew-academy__specsheet-tag-text">' + esc(tagLabel) + "</span>" +
                  (tagIcon ? '<span class="unew-academy__specsheet-tag-ico" aria-hidden="true">' + esc(tagIcon) + "</span>" : "") +
                "</div>"
              : ""
            ) +
          "</div>" +
          sectionsHtml +
        "</div>" +
      "</div>"
    );
  }

  function renderStandardProductHTML(cfg) {
    var title = cfg.title || "";
    var image = cfg.image || "";
    var shopUrl = cfg.shop_url || "";
    var shopLabel = cfg.shop_label || "Produkt im Shop öffnen";

    // SDS (optional)
    var sdsUrl = cfg.sds_url || "";
    var sdsLabel = cfg.sds_label || "SDS herunterladen";

    var mediaHtml = renderSingleMediaHTML(cfg);
    var audioUrl = (!mediaHtml && cfg.audio) ? cfg.audio : "";
    var badges = normalizeBadges(cfg.badges);
    var sections = Array.isArray(cfg.sections) ? cfg.sections : [];
    var noticeTitle = cfg.notice_title || "";
    var noticeLines = Array.isArray(cfg.notice) ? cfg.notice : [];

    var badgesHtml = badges.length
      ? '<div class="unew-academy__badges">' +
          badges.map(function (obj) { return renderBadgeHTML(obj); }).join("") +
        "</div>"
      : "";

    var sectionsHtml = sections.map(function (sec) {
      if (!sec || typeof sec !== "object") return "";
      var st = sec.title || "";
      var items = Array.isArray(sec.items) ? sec.items : [];
      if (!st && !items.length) return "";
      return (
        '<div class="unew-academy__section">' +
          '<div class="unew-academy__section-title">' + esc(st) + "</div>" +
          '<ul class="unew-academy__list">' +
            items.map(function (it) { return "<li>" + fmtInline(it) + "</li>"; }).join("") +
          "</ul>" +
        "</div>"
      );
    }).join("");

    var noticeHtml = (noticeTitle || noticeLines.length)
      ? '<div class="unew-academy__section unew-academy__section--notice">' +
          '<div class="unew-academy__section-title">' + esc(noticeTitle || "Wichtiger Hinweis") + "</div>" +
          '<ul class="unew-academy__list">' +
            (noticeLines.length
              ? noticeLines.map(function (l) { return "<li>" + fmtInline(l) + "</li>"; }).join("")
              : "<li>-</li>") +
          "</ul>" +
        "</div>"
      : "";

    var audioHtml = "";
    if (audioUrl) {
      var mime = guessAudioMime(audioUrl);
      audioHtml =
        '<div class="unew-academy__audio-wrap">' +
          '<audio class="unew-academy__audio" controls preload="metadata" playsinline>' +
            '<source src="' + esc(audioUrl) + '" type="' + esc(mime) + '">' +
          "</audio>" +
        "</div>";
    }

    // SDS link (optional) – shown in left media stack
    var sdsHtml = sdsUrl
      ? '<a class="unew-academy__product-shoplink unew-academy__product-shoplink--sds" href="' + esc(sdsUrl) + '" target="_blank" rel="noopener noreferrer">' +
          esc(sdsLabel) +
        "</a>"
      : "";

    var cornerHtml = renderCornerImageHTML(cfg);

    return (
      '<div class="unew-academy__product-card">' +
        '<div class="unew-academy__product-grid unew-academy__product-grid--v2">' +

          '<div class="unew-academy__product-header">' +
            '<div class="unew-academy__product-header-left">' +
              '<h2 class="unew-academy__product-title">' + esc(title) + "</h2>" +
            "</div>" +
            '<div class="unew-academy__product-header-mid">' +
              (badgesHtml || "") +
            "</div>" +
            '<div class="unew-academy__product-header-right">' +
              (cornerHtml || "") +
            "</div>" +
          "</div>" +

          '<div class="unew-academy__product-media">' +
            '<div class="unew-academy__product-img-wrap">' +
              (image ? '<img class="unew-academy__product-img" src="' + esc(image) + '" alt="' + esc(title) + '" loading="lazy">' : "") +
            "</div>" +
            (mediaHtml || audioHtml) +
            (shopUrl
              ? '<a class="unew-academy__product-shoplink" href="' + esc(shopUrl) + '" target="_blank" rel="noopener noreferrer">' +
                  esc(shopLabel) +
                "</a>"
              : ""
            ) +
            (sdsHtml || "") +
          "</div>" +

          '<div class="unew-academy__product-body">' +
            sectionsHtml +
            noticeHtml +
          "</div>" +

        "</div>" +
      "</div>"
    );
  }

  function renderProducts() {
    var blocks = document.querySelectorAll(SEL.PROD_BLOCK);
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (block.getAttribute("data-unew-product-done") === "1") continue;

      var cfg = parseJsonScript(block.querySelector(SEL.PROD_CFG));
      if (!cfg) {
        block.setAttribute("data-unew-product-done", "1");
        continue;
      }

      var target = ensureTarget(block, SEL.PROD_TARGET, "unew-academy__product-render");
      if (!isEmpty(target)) {
        block.setAttribute("data-unew-product-done", "1");
        continue;
      }

      var layout = String(cfg.layout || "product").trim().toLowerCase();
      target.innerHTML = (layout === "specsheet")
        ? renderSpecSheetHTML(cfg)
        : renderStandardProductHTML(cfg);

      block.setAttribute("data-unew-product-done", "1");
    }
  }

  // -------------------------------------------------------
  // INFO + AUDIO (mini-player always visible)
  // -------------------------------------------------------
  function renderInfoAudioBlocks() {
    var blocks = document.querySelectorAll(SEL.INFO_BLOCK);
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (block.getAttribute("data-unew-infoaudio-done") === "1") continue;

      var cfg = parseJsonScript(block.querySelector(SEL.INFO_CFG));
      if (!cfg) {
        block.setAttribute("data-unew-infoaudio-done", "1");
        continue;
      }

      var target = ensureTarget(block, SEL.INFO_TARGET, "unew-academy__infoaudio-render");
      if (!isEmpty(target)) {
        block.setAttribute("data-unew-infoaudio-done", "1");
        continue;
      }

      var title = cfg.title || "";
      var text = cfg.text || "";
      var autoplay = cfg.autoplay === true;
      var sources = Array.isArray(cfg.sources) ? cfg.sources : [];
      var ctaLabel = cfg.cta_label || "Audio abspielen";

      var sourcesHtml = sources.map(function (s) {
        if (!s || !s.src) return "";
        var t = s.type || guessAudioMime(s.src);
        return '<source src="' + esc(s.src) + '" type="' + esc(t) + '">';
      }).join("");

      target.innerHTML =
        '<div class="unew-academy__infoaudio">' +
          '<div class="unew-academy__infoaudio-head">' +
            (title ? '<div class="unew-academy__infoaudio-title">' + esc(title) + '</div>' : '<div></div>') +
            '<button type="button" class="unew-academy__audio-cta" data-unew-infoaudio-cta>' + esc(ctaLabel) + '</button>' +
          '</div>' +
          (text ? '<div class="unew-academy__infoaudio-text">' + esc(text) + '</div>' : '') +
          '<div class="unew-academy__infoaudio-player">' +
            '<audio class="unew-academy__audio unew-academy__audio--mini" controls preload="metadata" playsinline>' +
              sourcesHtml +
            '</audio>' +
          '</div>' +
        '</div>';

      var audio = target.querySelector("audio");
      var btn = target.querySelector("[data-unew-infoaudio-cta]");
      if (!audio || !btn) {
        block.setAttribute("data-unew-infoaudio-done", "1");
        continue;
      }

      function setBtnPlay() { btn.textContent = ctaLabel; }
      function setBtnPause() { btn.textContent = "Pause"; }

      function tryPlay() {
        var p = audio.play();
        if (p && typeof p.then === "function") {
          p.then(function () { setBtnPause(); })
           .catch(function () { setBtnPlay(); });
        }
      }

      btn.addEventListener("click", function () {
        if (audio.paused) tryPlay();
        else { audio.pause(); setBtnPlay(); }
      });

      audio.addEventListener("play", setBtnPause);
      audio.addEventListener("pause", setBtnPlay);
      audio.addEventListener("ended", setBtnPlay);

      if (autoplay) {
        tryPlay();
        var once = function () { tryPlay(); };
        window.addEventListener("touchstart", once, { passive: true, once: true });
        window.addEventListener("mousedown", once, { passive: true, once: true });
        window.addEventListener("keydown", once, { once: true });
      }

      block.setAttribute("data-unew-infoaudio-done", "1");
    }
  }

  // -------------------------------------------------------
  // Scheduler
  // -------------------------------------------------------
  var scheduled = false;

  function scan() {
    renderHeroes();
    renderSubHeros();
    renderProducts();
    renderInfoAudioBlocks();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      scan();
    });
  }

  function boot() {
    schedule();

    var mo = new MutationObserver(function () { schedule(); });
    mo.observe(document.documentElement, { childList: true, subtree: true });

    window.UNEWAcd = window.UNEWAcd || {};
    window.UNEWAcd.refresh = function () { schedule(); };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
